// ============================================================================
// STYLED EXCEL EXPORT UTILITY
// ============================================================================
// Shared workbook builder for consistent, professional Excel exports.

import * as XLSX from 'xlsx-js-style';

export interface ExportFilter { label: string; value: string; }

// ── Border tokens ─────────────────────────────────────────────────────────
const THIN_BORDER   = { style: 'thin', color: { rgb: 'D0D7E2' } } as const;
const HEADER_BORDER = { style: 'thin', color: { rgb: '15294A' } } as const;

// ── Shared fills ──────────────────────────────────────────────────────────
const BANNER_FILL = { fgColor: { rgb: 'DCE6F1' }, patternType: 'solid' } as const;
const FILTER_FILL = { fgColor: { rgb: 'F7F9FC' }, patternType: 'solid' } as const;

// ── Cell styles ───────────────────────────────────────────────────────────

const BANNER_TITLE_STYLE = {
  font: { bold: true, sz: 14, name: 'Calibri', color: { rgb: '1E3A5F' } },
  fill: BANNER_FILL,
  alignment: { horizontal: 'center', vertical: 'center' },
};

const BANNER_DATE_STYLE = {
  font: { italic: true, sz: 10, name: 'Calibri', color: { rgb: '6E7C8C' } },
  fill: BANNER_FILL,
  alignment: { horizontal: 'center', vertical: 'center' },
};

const FILTER_SECTION_STYLE = {
  font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '1E3A5F' } },
  fill: { fgColor: { rgb: 'EEF3FA' }, patternType: 'solid' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const FILTER_LABEL_STYLE = {
  font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '444444' } },
  fill: FILTER_FILL,
  alignment: { horizontal: 'left', vertical: 'center' },
};

const FILTER_VALUE_STYLE = {
  font: { sz: 10, name: 'Calibri', color: { rgb: '222222' } },
  fill: FILTER_FILL,
  alignment: { horizontal: 'left', vertical: 'center' },
};

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
  fill: { fgColor: { rgb: '1E3A5F' }, patternType: 'solid' },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
  border: { top: HEADER_BORDER, bottom: HEADER_BORDER, left: HEADER_BORDER, right: HEADER_BORDER },
};

const DATA_STYLE = {
  font: { sz: 10, name: 'Calibri' },
  alignment: { vertical: 'center' },
  border: { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER },
};

const NUMBER_STYLE = {
  font: { sz: 10, name: 'Calibri' },
  alignment: { vertical: 'center', horizontal: 'right' },
  border: { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER },
};

// ============================================================================
// BUILD STYLED WORKBOOK
// ============================================================================

export function buildStyledWorkbook(
  reportTitle: string,
  sheetName: string,
  rows: Record<string, unknown>[],
  numericColumns: string[] = [],
  activeFilters: ExportFilter[] = [],
): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();

  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const colCount = Math.max(headers.length, 2);

  const empty = (n: number) => Array(Math.max(n, 0)).fill('');

  const aoa: unknown[][] = [];
  const rowHeights: { hpx: number }[] = [];
  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];

  type RowKind = 'bannerTitle' | 'bannerDate' | 'filterSection' | 'filterRow' | 'colHeader' | 'data';
  const rowKinds: RowKind[] = [];

  const exportDate = new Date().toLocaleDateString('en-IN', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  // ── Row 0: report title — merged, centred ─────────────────────────────────
  aoa.push([reportTitle, ...empty(colCount - 1)]);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  rowHeights.push({ hpx: 28 });
  rowKinds.push('bannerTitle');

  // ── Row 1: export date — merged, centred ──────────────────────────────────
  aoa.push([`Exported on: ${exportDate}`, ...empty(colCount - 1)]);
  merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });
  rowHeights.push({ hpx: 20 });
  rowKinds.push('bannerDate');

  // ── Filter section ────────────────────────────────────────────────────────
  if (activeFilters.length > 0) {
    aoa.push(['Applied Filters:', ...empty(colCount - 1)]);
    merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: colCount - 1 } });
    rowHeights.push({ hpx: 20 });
    rowKinds.push('filterSection');

    for (const f of activeFilters) {
      aoa.push([`${f.label}:`, f.value, ...empty(colCount - 2)]);
      rowHeights.push({ hpx: 18 });
      rowKinds.push('filterRow');
    }
  }

  // ── Column header row ────────────────────────────────────────────────────
  const colHeaderRowIdx = aoa.length;
  aoa.push(headers.length > 0 ? headers : empty(colCount));
  rowHeights.push({ hpx: 22 });
  rowKinds.push('colHeader');

  // ── Data rows ────────────────────────────────────────────────────────────
  for (const row of rows) {
    aoa.push(headers.map((h) => row[h] ?? ''));
    rowHeights.push({ hpx: 18 });
    rowKinds.push('data');
  }
  if (rows.length === 0) {
    aoa.push(empty(colCount));
    rowHeights.push({ hpx: 18 });
    rowKinds.push('data');
  }

  // ── Build worksheet ─────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = merges;
  ws['!rows'] = rowHeights;

  // Auto-fit column widths
  if (headers.length > 0) {
    ws['!cols'] = headers.map((header) => {
      const maxLen = Math.max(
        header.length,
        ...rows.map((row) => String(row[header] ?? '').length),
      );
      return { wch: Math.min(maxLen + 4, 55) };
    });
  }

  // ── Apply cell styles ───────────────────────────────────────────────────
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');

  for (let R = range.s.r; R <= range.e.r; R++) {
    const kind = rowKinds[R];
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });

      if (kind === 'bannerTitle' || kind === 'bannerDate') {
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = C === 0
          ? (kind === 'bannerTitle' ? BANNER_TITLE_STYLE : BANNER_DATE_STYLE)
          : { fill: BANNER_FILL };
        continue;
      }

      if (!ws[addr]) continue;

      switch (kind) {
        case 'filterSection':
          ws[addr].s = FILTER_SECTION_STYLE;
          break;
        case 'filterRow':
          ws[addr].s = C === 0 ? FILTER_LABEL_STYLE : FILTER_VALUE_STYLE;
          break;
        case 'colHeader':
          ws[addr].s = HEADER_STYLE;
          break;
        case 'data':
          if (R > colHeaderRowIdx) {
            ws[addr].s = numericColumns.includes(headers[C]) ? NUMBER_STYLE : DATA_STYLE;
          }
          break;
        default: break;
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return wb;
}

// ============================================================================
// CONVENIENCE: Export directly to file
// ============================================================================

export function exportStyledExcel(
  reportTitle: string,
  sheetName: string,
  rows: Record<string, unknown>[],
  fileName: string,
  numericColumns: string[] = [],
  activeFilters: ExportFilter[] = [],
): void {
  const wb = buildStyledWorkbook(reportTitle, sheetName, rows, numericColumns, activeFilters);
  const dateSuffix = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `${fileName}-${dateSuffix}.xlsx`);
}
